import React, { useEffect, useState } from 'react';
import { Lead } from '../../../../types';
import {
    ClockIcon,
    UserIcon,
    FilterIcon,
    CurrencyDollarIcon,
    PencilIcon,
    CalendarIcon,
    ArrowRightIcon,
    ChatBubbleLeftRightIcon,
    CheckCircleIcon,
    XCircleIcon,
    ArrowPathIcon,
} from '../../../../components/icons';
import { leadHistoryService, LeadHistoryEvent } from '../../../../services/leadHistoryService';
import { useUsers } from '../../../../contexts/users/UsersContext';
import { cn } from '@/lib/utils';

interface LeadHistoryTabProps {
    lead: Lead;
}

const getEventStyle = (type: LeadHistoryEvent['type']): string => {
    switch (type) {
        case 'won':
            return 'bg-accent-subtle text-accent';
        case 'lost':
            return 'bg-danger-bg text-danger-text';
        case 'reopened':
            return 'bg-highlight-subtle text-highlight';
        case 'agent_assigned':
            return 'bg-primary-subtle text-primary';
        case 'agent_removed':
            return 'bg-highlight-subtle text-highlight';
        case 'stage_changed':
        case 'created':
        case 'value_updated':
        case 'task_created':
        case 'task_completed':
        case 'task_uncompleted':
        case 'conversation_started':
        case 'conversation_ended':
            return 'bg-primary-subtle text-primary';
        default:
            return 'bg-surface-subtle text-secondary';
    }
};

const getEventIcon = (type: LeadHistoryEvent['type']): React.ReactNode => {
    const iconClass = 'size-3.5';
    switch (type) {
        case 'created':
        case 'stage_changed':
            return <FilterIcon className={iconClass} />;
        case 'agent_assigned':
        case 'agent_removed':
            return <UserIcon className={iconClass} />;
        case 'value_updated':
            return <CurrencyDollarIcon className={iconClass} />;
        case 'won':
            return <CheckCircleIcon className={iconClass} />;
        case 'lost':
            return <XCircleIcon className={iconClass} />;
        case 'reopened':
            return <ArrowPathIcon className={iconClass} />;
        case 'task_created':
        case 'task_completed':
        case 'task_uncompleted':
            return <CalendarIcon className={iconClass} />;
        case 'conversation_started':
        case 'conversation_ended':
            return <ChatBubbleLeftRightIcon className={iconClass} />;
        default:
            return <PencilIcon className={iconClass} />;
    }
};

export const LeadHistoryTab: React.FC<LeadHistoryTabProps> = ({ lead }) => {
    const [historyEvents, setHistoryEvents] = useState<LeadHistoryEvent[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const { crmUsers } = useUsers();

    useEffect(() => {
        let isCancelled = false;
        const loadHistory = async () => {
            setIsLoading(true);
            try {
                const events = await leadHistoryService.getLeadHistory(lead.id);
                if (!isCancelled) setHistoryEvents(events);
            } catch (error) {
                if (!isCancelled) console.error('[LeadHistoryTab] Erro ao carregar histórico:', error);
            } finally {
                if (!isCancelled) setIsLoading(false);
            }
        };
        void loadHistory();
        return () => {
            isCancelled = true;
        };
    }, [lead.id]);

    const formatTimestamp = (date: Date) =>
        new Intl.DateTimeFormat('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        }).format(date);

    const formatValue = (value: string | undefined, type: LeadHistoryEvent['type']) => {
        if (!value) return '';
        if (type === 'agent_assigned' || type === 'agent_removed' || type === 'created') {
            const user = crmUsers.find((u) => u.id === value);
            return user ? user.name : value;
        }
        return value;
    };

    const formatEventDescription = (event: LeadHistoryEvent) => {
        if (event.type !== 'created') return event.description;
        const creatorId = event.userId || lead.createdBy;
        const creatorName = creatorId
            ? crmUsers.find((u) => u.id === creatorId)?.name
            : lead.creatorName;
        return creatorName ? `${event.description} por ${creatorName}` : event.description;
    };

    if (isLoading) {
        return (
            <div
                className="flex flex-col items-center justify-center gap-s3 py-s16"
                role="status"
                aria-live="polite"
            >
                <div
                    className="size-7 animate-spin rounded-full border-2 border-primary border-t-transparent"
                    aria-hidden
                />
                <p className="text-sm text-secondary">Carregando histórico...</p>
            </div>
        );
    }

    if (historyEvents.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center gap-s2 py-s16 text-center">
                <ClockIcon className="size-10 text-muted opacity-40" aria-hidden />
                <p className="text-sm font-medium text-default">Nenhum histórico registrado</p>
                <p className="text-xs text-muted">As alterações aparecerão aqui automaticamente</p>
            </div>
        );
    }

    return (
        <ol className="relative m-0 list-none space-y-s6 py-s2" aria-label="Histórico de eventos">
            <div
                className="absolute bottom-s2 left-[15px] top-s2 w-px bg-border"
                aria-hidden
            />

            {historyEvents.map((event) => (
                <li key={event.id} className="relative flex gap-s4">
                    <div
                        className={cn(
                            'relative z-10 flex size-8 shrink-0 items-center justify-center rounded-full',
                            getEventStyle(event.type),
                        )}
                        aria-hidden
                    >
                        {getEventIcon(event.type)}
                    </div>

                    <div className="min-w-0 flex-1 pt-s1">
                        <div className="mb-s1 flex items-start justify-between gap-s3">
                            <p className="text-sm font-medium leading-snug text-default">
                                {formatEventDescription(event)}
                            </p>
                            <time
                                className="mt-s1 shrink-0 whitespace-nowrap font-mono text-2xs tabular-nums text-muted"
                                dateTime={event.timestamp.toISOString()}
                            >
                                {formatTimestamp(event.timestamp)}
                            </time>
                        </div>

                        {(event.oldValue || event.newValue) && (
                            <div className="mt-s1 flex flex-wrap items-center gap-s1 text-xs">
                                {event.oldValue ? (
                                    <span className="inline-flex items-center rounded-md bg-danger-bg px-s2 py-s1 font-medium text-danger-text">
                                        {formatValue(event.oldValue, event.type)}
                                    </span>
                                ) : null}
                                {event.oldValue && event.newValue ? (
                                    <ArrowRightIcon
                                        className="size-3 shrink-0 text-muted"
                                        aria-label="para"
                                    />
                                ) : null}
                                {event.newValue ? (
                                    <span className="inline-flex items-center rounded-md bg-accent-subtle px-s2 py-s1 font-medium text-accent">
                                        {formatValue(event.newValue, event.type)}
                                    </span>
                                ) : null}
                            </div>
                        )}
                    </div>
                </li>
            ))}
        </ol>
    );
};
